import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import SignalCellularAltRounded from '@mui/icons-material/SignalCellularAltRounded';
import SignalCellularAlt2BarRounded from '@mui/icons-material/SignalCellularAlt2BarRounded';
import SignalCellularAlt1BarRounded from '@mui/icons-material/SignalCellularAlt1BarRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import SensorsOffRoundedIcon from '@mui/icons-material/SensorsOffRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import TerminalIcon from '@mui/icons-material/Terminal';
import FooterTogglesMenu from './FooterTogglesMenu';
import FooterNotificationBell from './FooterNotificationBell';
import CopilotUserMenu from '../CopilotUserMenu/CopilotUserMenu';
import { FONT_FAMILY } from '../../../constants/typography';
import { getBatteryStatus, monitorBatteryStatus } from '../../../utils/batteryUtils';
import { getConnectionStatus, monitorConnectionStatus } from '../../../utils/connectionUtils';
import { getInternetStatus, monitorInternetStatus } from '../../../utils/internetUtils';
import packageJson from '../../../../package.json';



const STATUS_TONE_MAP = {
  ok: { bg: '#1FC760', icon: CheckRoundedIcon, iconColor: '#040404', textColor: 'rgba(245,246,249,0.92)' },
  warning: { bg: '#F4A336', icon: RemoveRoundedIcon, iconColor: '#040404', textColor: 'rgba(245,246,249,0.92)' },
  error: { bg: '#E63C2F', icon: CloseRoundedIcon, iconColor: '#FFFFFF', textColor: '#FE8371' },
};

const DEFAULT_GROUPS = [[
  { label: 'Internet', value: 'Connected', tone: 'ok' },
  { label: 'Connection', value: 'Good', tone: 'ok' },
  { label: 'Battery', value: 'N/A', tone: 'ok' },
]];

// Sub-components
const StatusIndicator = ({ label, value, tone = 'ok', variant = 'solid' }) => {
  const config = STATUS_TONE_MAP[tone] ?? STATUS_TONE_MAP.ok;
  const Icon = config.icon;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: variant === 'dashed' ? 2 : 1,
        border: variant === 'dashed' ? '2px dashed rgba(230, 60, 47, 0.9)' : '1px solid transparent',
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          bgcolor: config.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }}
      >
        <Icon sx={{ fontSize: 16, color: config.iconColor }} />
      </Box>
      <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14, fontWeight: 500, letterSpacing: '0.015em', color: config.textColor }}>
        {label}: <Typography component="span" sx={{ fontWeight: 700, color: config.textColor }}>{value}</Typography>
      </Typography>
    </Stack>
  );
};

StatusIndicator.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  tone: PropTypes.oneOf(['ok', 'warning', 'error']),
  variant: PropTypes.oneOf(['solid', 'dashed']),
};

const VerticalDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center', borderColor: 'rgba(255,255,255,0.12)', mx: 1 }} />
);

// Tone → colour used by the compact (copilot) status icons.
const TONE_COLOR = { ok: '#1FC760', warning: '#F4A336', error: '#E63C2F', off: 'rgba(245,246,249,0.45)' };

// Pick the cellular icon by signal tone (full / 2-bar / 1-bar ascending bars).
const signalIconFor = (tone) =>
  tone === 'error' ? SignalCellularAlt1BarRounded
    : tone === 'warning' ? SignalCellularAlt2BarRounded
      : SignalCellularAltRounded;

// STOMP broker connection state (from useStompService, threaded down through
// TXLive -> TXLivePage -> AppRoutes) mapped to a footer signal. The broker only
// connects while Live is on, so 'idle' is the expected state with Live off —
// it means "no connection attempted", not "service is down". Always rendered
// (never hidden) so a missing icon can't be mistaken for a healthy connection.
// 'reconnecting' means the client was connected before and stompjs's own
// reconnectDelay loop is now redialing.
const BROKER_STATUS_CONFIG = {
  idle: { tone: 'off', label: 'Not connected', Icon: SensorsOffRoundedIcon },
  connecting: { tone: 'warning', label: 'Connecting…', Icon: SensorsRoundedIcon },
  connected: { tone: 'ok', label: 'Connected', Icon: SensorsRoundedIcon },
  reconnecting: { tone: 'error', label: 'Disconnected — reconnecting…', Icon: SensorsOffRoundedIcon },
};

// Compact icon-only status for the copilot footer: cellular signal icon
// (internet/connection) plus, when live, the broker connection icon —
// replacing the verbose text rows.
const CompactStatusCluster = ({ connection, internet, brokerStatus }) => {
  const internetDown = internet?.tone === 'error';
  const sigTone = internetDown ? 'error' : (connection?.tone || 'ok');
  const SignalIcon = signalIconFor(sigTone);
  const broker = BROKER_STATUS_CONFIG[brokerStatus] ?? BROKER_STATUS_CONFIG.idle;
  return (
    <Stack direction="row" spacing={1.75} alignItems="center">
      <VerticalDivider />
      <Tooltip title={`Internet: ${internet?.value || '—'} · Connection: ${connection?.value || '—'}`}>
        <Box sx={{ display: 'flex' }}>
          <SignalIcon sx={{ fontSize: 24, color: TONE_COLOR[sigTone] ?? TONE_COLOR.ok }} />
        </Box>
      </Tooltip>
      <Box sx={{ display: 'flex' }} aria-label={`Event Service: ${broker.label}`}>
        <broker.Icon sx={{ fontSize: 24, color: TONE_COLOR[broker.tone] }} />
      </Box>
    </Stack>
  );
};

CompactStatusCluster.propTypes = { connection: PropTypes.object, internet: PropTypes.object, brokerStatus: PropTypes.string };


// Main Component
const Footer = ({
  pullInfo,
  leadingStatuses = [],
  groups: propGroups = DEFAULT_GROUPS,
  refreshAction,
  onLogout,
  selectionDisplay,
  onFixtureCogClick,
  onRendererCogClick,
  fixtureOverlayOpen = false,
  rendererOverlayOpen = false,
  toggles = [],
  selectors = [],
  lineupErrors = [],
  onLineupErrorsClick,
  onCommandConsoleClick,
  commandConsoleOpen = false,
  brokerStatus,
  compactStatus = false,
  // Copilot can't change the fixture: hide the cog (the fixture name stays as a
  // read-only label). Separate from compactStatus, which is now true for all roles.
  lockFixtureChange = false,
}) => {
  const [batteryStatus, setBatteryStatus] = useState({ value: 'N/A', tone: 'ok', level: 100 });
  const [connectionStatus, setConnectionStatus] = useState({ value: 'Good', tone: 'ok' });
  const [internetStatus, setInternetStatus] = useState({ value: 'Connected', tone: 'ok' });
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [changelogContent, setChangelogContent] = useState('');

  // Fetch changelog when dialog opens
  useEffect(() => {
    if (changelogOpen && !changelogContent) {
      // #13: curated, user-facing changelog (not the raw dev/commit log).
      fetch('/CHANGELOG.user.md')
        .then(res => res.text())
        .then(setChangelogContent)
        .catch(() => setChangelogContent('Failed to load changelog.'));
    }
  }, [changelogOpen, changelogContent]);

  // Initialize and monitor system statuses
  useEffect(() => {
    const initializeStatuses = async () => {
      const [battery, connection, internet] = await Promise.all([
        getBatteryStatus(),
        getConnectionStatus(),
        getInternetStatus(),
      ]);
      setBatteryStatus(battery);
      setConnectionStatus(connection);
      setInternetStatus(internet);
    };

    initializeStatuses();

    const cleanupBattery = monitorBatteryStatus(setBatteryStatus);
    const cleanupConnection = monitorConnectionStatus(setConnectionStatus);
    const cleanupInternet = monitorInternetStatus(setInternetStatus);

    return () => {
      cleanupBattery();
      cleanupConnection();
      cleanupInternet();
    };
  }, []);

  // Merge prop groups with real-time status data
  const groups = propGroups.map((group) =>
    group.map((item) => {
      if (item.label === 'Battery') return { ...item, value: batteryStatus.value, tone: batteryStatus.tone, variant: batteryStatus.level <= 20 ? 'dashed' : 'solid' };
      if (item.label === 'Connection') return { ...item, value: connectionStatus.value, tone: connectionStatus.tone };
      if (item.label === 'Internet') return { ...item, value: internetStatus.value, tone: internetStatus.tone };
      return item;
    })
  );

  // Copilot bottom sheet uses a larger base font + wider letter spacing for
  // at-a-distance readability.
  const footerFs = compactStatus ? 19 : 14;
  const getFooterTextSx = () => ({
    fontSize: footerFs,
    fontWeight: compactStatus ? 600 : 500,
    color: 'rgba(255,255,255,0.9)',
    fontFamily: FONT_FAMILY,
    letterSpacing: compactStatus ? '0.05em' : '0.015em',
    transition: 'color 0.2s ease',
  });

  // Lineup-checks eye icon: grey when clean, amber for warnings, red if any
  // hard error. Colour reflects the worst issue present.
  const hasHardError = lineupErrors.some((e) => e?.severity === 'error');
  const eyeColor = lineupErrors.length === 0
    ? 'rgba(255,255,255,0.4)'
    : hasHardError ? '#ff6b6b' : '#feec67';
  const eyeTooltip = lineupErrors.length === 0
    ? 'Lineup checks — all good'
    : `${lineupErrors.length} lineup issue${lineupErrors.length === 1 ? '' : 's'} — click to view`;

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        minHeight: 62,
        px: 4,
        backgroundColor: '#1E1E1E',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        color: '#FFFFFF',
        gap: 3,
        overflowX: 'auto',
      }}
    >
      {/* Pull Info & Leading Statuses */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* Copilot: user avatar (with logout menu) pinned at the bottom-left. */}
        {compactStatus && onLogout && (
          <CopilotUserMenu initials={pullInfo?.initials} name={pullInfo?.text} onLogout={onLogout} menuAlign="left" />
        )}
        {/* Copilot moves the user to the left avatar menu, so the name is hidden here. */}
        {pullInfo && !compactStatus && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#FFFFFF', color: '#121212', fontSize: 12, fontFamily: FONT_FAMILY, fontWeight: 500 }}>
              {pullInfo.initials ?? 'TR'}
            </Avatar>
            <Typography sx={{ fontSize: footerFs, fontWeight: 500, letterSpacing: '0.015em', fontFamily: FONT_FAMILY }}>
              {pullInfo.text}
            </Typography>
          </Stack>
        )}
        {leadingStatuses.map((status) => (
          <StatusIndicator key={`${status.label}-${status.value}`} {...status} />
        ))}
        {refreshAction && (
          <Button
            onClick={refreshAction.onClick}
            variant="contained"
            size="small"
            startIcon={<AutorenewRoundedIcon />}
            sx={{
              bgcolor: '#FEEC67',
              color: '#121212',
              textTransform: 'uppercase',
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              letterSpacing: '0.08em',
              px: 2.5,
              py: 0.75,
              borderRadius: 2,
              '&:hover': { bgcolor: '#ffe15a' },
            }}
          >
            {refreshAction.label}
          </Button>
        )}
      </Stack>

      {/* Status — compact icons (copilot) or the verbose text rows (drive). */}
      {compactStatus ? (
        <CompactStatusCluster connection={connectionStatus} internet={internetStatus} brokerStatus={brokerStatus} />
      ) : (
        groups.map((group, index) => (
          <Stack key={`footer-group-${index}`} direction="row" spacing={1.5} alignItems="center">
            <VerticalDivider />
            {group.map((status) => (
              <StatusIndicator key={`${status.label}-${status.value}`} {...status} />
            ))}
          </Stack>
        ))
      )}

      {/* Fixture & Renderer Selection */}
      {onLineupErrorsClick && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <VerticalDivider />
          <Tooltip title={eyeTooltip}>
            <IconButton
              onClick={onLineupErrorsClick}
              size="small"
              sx={{ color: eyeColor, transition: 'color 0.2s ease', '&:hover': { color: eyeColor, filter: 'brightness(1.2)' } }}
            >
              <Badge
                badgeContent={lineupErrors.length}
                color={hasHardError ? 'error' : 'warning'}
                overlap="circular"
                sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16, fontWeight: 700 } }}
              >
                <RuleRoundedIcon sx={{ fontSize: 22 }} />
              </Badge>
            </IconButton>
          </Tooltip>
        </Stack>
      )}
      {(onFixtureCogClick || onRendererCogClick) && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <VerticalDivider />
          {onFixtureCogClick && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {/* Copilot can't change the fixture — hide the cog but keep the
                  fixture name as a read-only label. (compactStatus is now true
                  for all roles, so this is gated on the dedicated flag.) */}
              {!lockFixtureChange && (
                <Tooltip title="Change fixture">
                  <IconButton
                    onClick={onFixtureCogClick}
                    size="small"
                    sx={{
                      color: fixtureOverlayOpen ? '#FEEC67' : 'rgba(255,255,255,0.6)',
                      transform: fixtureOverlayOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.4s ease-in-out, color 0.2s ease',
                      '&:hover': { color: '#FEEC67' },
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: compactStatus ? 20 : 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="View full fixture detail">
                <Box
                  component={selectionDisplay?.aetherUrl ? 'a' : 'span'}
                  href={selectionDisplay?.aetherUrl}
                  target={selectionDisplay?.aetherUrl ? '_blank' : undefined}
                  rel={selectionDisplay?.aetherUrl ? 'noopener noreferrer' : undefined}
                  sx={{
                    cursor: selectionDisplay?.aetherUrl ? 'pointer' : 'default',
                    textDecoration: 'none',
                    '&:hover': selectionDisplay?.aetherUrl ? { textDecoration: 'underline', '& .footer-text': { color: '#FEEC67' } } : {},
                  }}
                >
                  <Typography
                    className="footer-text"
                    sx={{
                      ...getFooterTextSx(),
                      textDecoration: 'inherit',
                    }}
                  >
                    {(compactStatus && selectionDisplay?.fixtureShort) || selectionDisplay?.fixture || 'Select fixture'}
                  </Typography>
                </Box>
              </Tooltip>
            </Stack>
          )}
          {onFixtureCogClick && onRendererCogClick && <VerticalDivider />}
          {onRendererCogClick && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Change renderer">
                <IconButton
                  onClick={onRendererCogClick}
                  size="small"
                  sx={{
                    color: rendererOverlayOpen ? '#FEEC67' : 'rgba(255,255,255,0.6)',
                    transform: rendererOverlayOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.4s ease-in-out, color 0.2s ease',
                    '&:hover': { color: '#FEEC67' },
                  }}
                >
                  <SettingsIcon sx={{ fontSize: compactStatus ? 20 : 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="View renderer detail">
                <Box
                  component={selectionDisplay?.rendererDetailUrl ? 'a' : 'span'}
                  href={selectionDisplay?.rendererDetailUrl}
                  target={selectionDisplay?.rendererDetailUrl ? '_blank' : undefined}
                  rel={selectionDisplay?.rendererDetailUrl ? 'noopener noreferrer' : undefined}
                  sx={{
                    cursor: selectionDisplay?.rendererDetailUrl ? 'pointer' : 'default',
                    textDecoration: 'none',
                    '&:hover': selectionDisplay?.rendererDetailUrl ? { textDecoration: 'underline', '& .footer-text': { color: '#FEEC67' } } : {},
                  }}
                >
                  <Typography
                    className="footer-text"
                    sx={{
                      ...getFooterTextSx(),
                      textDecoration: 'inherit',
                    }}
                  >
                    {selectionDisplay?.renderer || 'Select renderer'}
                  </Typography>
                </Box>
              </Tooltip>
              {selectionDisplay?.rendererPreviewUrl && (
                <Tooltip title="Open renderer preview">
                  <IconButton
                    component="a"
                    href={selectionDisplay.rendererPreviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#FEEC67' } }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
              {/* Soft renderer-lock badge — lives beside renderer selection in the
                  footer (moved out of TXLive's copilot tab row, where it crowded
                  the CLOCK/PLAYER/STATS/SP tabs). Read-only signal: actions are
                  still gated by the broker choke-point regardless of where this
                  badge renders. */}
              {selectionDisplay?.isRendererLocked && (
                <Tooltip
                  title={
                    selectionDisplay?.lockedByName
                      ? `This renderer is locked by ${selectionDisplay.lockedByName}. You can still view it, but graphics/broker actions are disabled until it's unlocked.`
                      : "This renderer is locked. You can still view it, but graphics/broker actions are disabled until it's unlocked."
                  }
                >
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 0.5 }}>
                    <LockIcon sx={{ fontSize: 15, color: '#F4A336' }} />
                    <Typography
                      sx={{
                        ...getFooterTextSx(),
                        color: '#F4A336',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {selectionDisplay?.lockedByName ? `Renderer Locked by ${selectionDisplay.lockedByName}` : 'Renderer Locked'}
                    </Typography>
                  </Stack>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      )}

      {/* Toggles */}
      {(toggles.length > 0 || selectors.length > 0 || onCommandConsoleClick) && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ml: 'auto' }}>
          <VerticalDivider />
          {/* OPUS-1527 — operator messages land here now instead of in a toast
              overlay that covered the controls. Self-contained: it subscribes to
              utils/notificationLog itself, so it needs no props from AppRoutes. */}
          <FooterNotificationBell compactStatus={compactStatus} />
          <FooterTogglesMenu toggles={toggles} selectors={selectors} compactStatus={compactStatus} />
          {onCommandConsoleClick && (
            <Tooltip title="Command Console">
              <IconButton
                onClick={onCommandConsoleClick}
                size="small"
                sx={{
                  color: commandConsoleOpen ? '#FEEC67' : 'rgba(255,255,255,0.6)',
                  '&:hover': { color: '#FEEC67' },
                }}
              >
                <TerminalIcon sx={{ fontSize: compactStatus ? 20 : 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}

      {/* Version & Logout */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ml: (toggles.length > 0 || selectors.length > 0) ? 0 : 'auto' }}>
        <VerticalDivider />
        <Tooltip title="View changelog">
          <Box
            component="button"
            onClick={() => setChangelogOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              p: 0,
              '&:hover': { '& .version-text': { color: '#FEEC67' } },
            }}
          >
            <HistoryIcon sx={{ fontSize: compactStatus ? 18 : 14, color: '#FFFFFF' }} />
            <Typography
              className="version-text"
              sx={{ fontSize: compactStatus ? 15 : 12, fontWeight: 500, color: 'rgba(255,255,255,0.5)', fontFamily: FONT_FAMILY, letterSpacing: '0.02em', transition: 'color 0.2s ease' }}
            >
              v{packageJson.serviceVersion}
            </Typography>
          </Box>
        </Tooltip>
        {/* Copilot puts the user avatar (with logout menu) at the bottom-left;
            drive keeps the plain logout icon here. */}
        {onLogout && !compactStatus && (
          <>
            <VerticalDivider />
            <Tooltip title="Logout from current session">
              <IconButton
                onClick={onLogout}
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#FEEC67', bgcolor: 'rgba(254,236,103,0.1)' } }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>

      {/* Changelog Dialog */}
      <Dialog
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { bgcolor: '#1E1E1E', color: '#FFFFFF', borderRadius: 2, maxHeight: '80vh' } } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: '#FFFFFF' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Changelog</Typography>
          </Box>
          <IconButton onClick={() => setChangelogOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ fontFamily: FONT_FAMILY, fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', color: 'rgba(255,255,255,0.8)' }}>
            {changelogContent || 'Loading...'}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

Footer.propTypes = {
  pullInfo: PropTypes.shape({ initials: PropTypes.string, text: PropTypes.string }),
  leadingStatuses: PropTypes.array,
  groups: PropTypes.array,
  refreshAction: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }),
  onLogout: PropTypes.func,
  selectionDisplay: PropTypes.shape({
    fixture: PropTypes.string,
    renderer: PropTypes.string,
    aetherUrl: PropTypes.string,
    rendererDetailUrl: PropTypes.string,
    rendererPreviewUrl: PropTypes.string,
    isRendererLocked: PropTypes.bool,
    lockedByName: PropTypes.string,
  }),
  onFixtureCogClick: PropTypes.func,
  onRendererCogClick: PropTypes.func,
  fixtureOverlayOpen: PropTypes.bool,
  rendererOverlayOpen: PropTypes.bool,
  toggles: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.bool,
    onChange: PropTypes.func,
    tooltip: PropTypes.string,
    // In use since the NLE toggle shipped, but never declared — an unknown key
    // in a shape is permitted, so the omission hid an in-use prop from readers.
    disabled: PropTypes.bool,
  })),
  selectors: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    options: PropTypes.array,
    onChange: PropTypes.func,
  })),
  lineupErrors: PropTypes.arrayOf(PropTypes.shape({
    team: PropTypes.string,
    severity: PropTypes.oneOf(['error', 'warning']),
    message: PropTypes.string,
  })),
  onLineupErrorsClick: PropTypes.func,
  onCommandConsoleClick: PropTypes.func,
  commandConsoleOpen: PropTypes.bool,
  brokerStatus: PropTypes.oneOf(['idle', 'connecting', 'connected', 'reconnecting']),
  compactStatus: PropTypes.bool,
  lockFixtureChange: PropTypes.bool,
};

export default Footer;
